/**
 * A small PIXELATED Discord logo pinned to the bottom-right corner of the pre-game screens
 * (login / register / character select), linking to our server invite. The icon is an AI-generated
 * black-and-white pixel-art Discord mark with a transparent background (assets/ui/discord.png),
 * rendered crisp via `image-rendering: pixelated`. Toggle with show/hide — hidden once the player
 * enters the 3D world.
 */
import { assetUrl } from "../assetVer.js";

const INVITE_URL = "https://discord.gg/xMAwUcDpXN";
const ICON_URL = assetUrl("/assets/ui/discord.png");
const STYLE_ID = "gl-discord-badge-style";

/** Inject the periodic "attention" keyframes once: idle most of the cycle, then a quick shake + pop
 *  every ~6s so the badge catches the eye without being constantly distracting. */
function ensureStyle(): void {
  if (document.getElementById(STYLE_ID)) return;
  const s = document.createElement("style");
  s.id = STYLE_ID;
  s.textContent = `
    @keyframes gl-discord-attn {
      0%, 70%, 100% { transform: scale(1) rotate(0deg); }
      77% { transform: scale(1.22) rotate(-12deg); }
      83% { transform: scale(1.22) rotate(12deg); }
      89% { transform: scale(1.16) rotate(-8deg); }
      95% { transform: scale(1.1) rotate(5deg); }
    }
    #gl-discord-badge { animation: gl-discord-attn 6s ease-in-out infinite; transform-origin: center; }
    #gl-discord-badge:hover { animation-play-state: paused; }
    @media (prefers-reduced-motion: reduce) { #gl-discord-badge { animation: none; } }
  `;
  document.head.appendChild(s);
}

let el: HTMLAnchorElement | null = null;

/** Show the Discord badge (creates it on first call). Idempotent across the pre-game screens. */
export function showDiscordBadge(): void {
  if (!el) {
    ensureStyle();
    el = document.createElement("a");
    el.id = "gl-discord-badge";
    el.href = INVITE_URL;
    el.target = "_blank";
    el.rel = "noopener noreferrer";
    el.title = "Join our Discord";
    el.setAttribute("aria-label", "Join our Discord");
    Object.assign(el.style, {
      position: "fixed", right: "18px", bottom: "18px", width: "72px", height: "72px",
      backgroundImage: `url("${ICON_URL}")`, backgroundSize: "contain",
      backgroundRepeat: "no-repeat", backgroundPosition: "center",
      imageRendering: "pixelated", zIndex: "10000", cursor: "pointer",
      opacity: "0.85", transition: "opacity .15s ease",
      filter: "drop-shadow(0 2px 4px rgba(0,0,0,.55))",
    } as Partial<CSSStyleDeclaration>);
    el.addEventListener("mouseenter", () => { if (el) el.style.opacity = "1"; });
    el.addEventListener("mouseleave", () => { if (el) el.style.opacity = "0.85"; });
    document.body.appendChild(el);
  }
  el.style.display = "block";
}

/** Hide the Discord badge (e.g. once the player enters the world). */
export function hideDiscordBadge(): void {
  if (el) el.style.display = "none";
}
